import { ColorGenerator } from "./colors";
import {
  CategoryData,
  HoverState,
  InteractiveLineChartData,
  LocationKeywordData,
  SelectionState,
  TimeSeriesDataPoint
} from "./types";

export const transformKeywordData = (
  data: LocationKeywordData[]
): InteractiveLineChartData => {
  const colorGenerator = new ColorGenerator();

  const categories: CategoryData[] = data.map((location) => {
    const keywordCounts: { [keyword: string]: number } = {};

    Object.values(location.data).forEach((yearData) => {
      Object.values(yearData).forEach((monthData) => {
        monthData.forEach((item) => {
          if (!keywordCounts[item.keyword]) {
            keywordCounts[item.keyword] = 0;
          }
          keywordCounts[item.keyword] += Number(item.value);
        });
      });
    });

    const sortedKeywords = Object.entries(keywordCounts)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 10)
      .map((entry) => entry[0]);

    const items = sortedKeywords.map((keyword) => {
      const color = colorGenerator.getColorForLabel(
        keyword,
        location.location_name
      );
      return {
        label: keyword,
        key: `${location.location_id}_${keyword}`,
        color
      };
    });

    return {
      categoryName: location.location_name,
      items
    };
  });

  const allTimePoints = new Set<string>();

  data.forEach((location) => {
    Object.entries(location.data).forEach(([year, yearData]) => {
      Object.keys(yearData).forEach((month) => {
        allTimePoints.add(`${year}-${month}`);
      });
    });
  });

  const sortedTimePoints = Array.from(allTimePoints).sort((a, b) => {
    const [yearA, monthA] = a.split("-").map(Number);
    const [yearB, monthB] = b.split("-").map(Number);

    if (yearA !== yearB) {
      return yearA - yearB;
    }
    return monthA - monthB;
  });

  const timeSeriesData: TimeSeriesDataPoint[] = sortedTimePoints.map(
    (timePoint) => {
      const [year, month] = timePoint.split("-");
      const values: { [key: string]: { value: string | number } } = {};

      data.forEach((location) => {
        if (location.data[year] && location.data[year][month]) {
          location.data[year][month].forEach((item) => {
            const key = `${location.location_id}_${item.keyword}`;
            values[key] = { value: item.value };
          });
        }
      });

      return {
        timestamp: timePoint,
        values
      };
    }
  );

  return {
    categories,
    timeSeriesData
  };
};

export const formatTimeLabel = (timestamp: string): string => {
  const [year, month] = timestamp.split("-");
  return `${year}年${month}月`;
};

export const tooltipFormatter = (value: number | string): string => {
  return new Intl.NumberFormat("ja").format(Number(value));
};

/**
 * Process and format chart tooltip items
 * Based on current hover state and timestamp, returns tooltip items that should be displayed
 *
 * @param payload Original payload data provided by Recharts
 * @param timestamp Current timestamp
 * @param data Chart data
 * @param hoverState Current hover state
 * @param selectionState Selection state for filtering items
 * @returns Processed tooltip items array, sorted by value in descending order
 */
export const processTooltipItems = <T extends string>(
  payload: any[],
  timestamp: string,
  data: InteractiveLineChartData<T>,
  hoverState: HoverState,
  selectionState?: SelectionState
): any[] => {
  const timePoint = data.timeSeriesData.find(
    (point) => point.timestamp === timestamp
  );

  if (!timePoint) return [];

  const currentTimePointKeys = new Set<string>();
  const visibleItems: any[] = [];

  data.categories.forEach((category) => {
    category.items.forEach((item) => {
      let shouldShow = true;

      if (selectionState && selectionState.hasSelection) {
        const isLabelSelected = selectionState.selectedLabels.has(item.label);
        const isCategorySelected = selectionState.selectedCategories.has(
          category.categoryName
        );

        if (selectionState.selectedLabels.size > 0 && !isLabelSelected) {
          shouldShow = false;
        }

        if (
          selectionState.selectedCategories.size > 0 &&
          selectionState.selectedLabels.size === 0 &&
          !isCategorySelected
        ) {
          shouldShow = false;
        }
      } else {
        shouldShow =
          (!hoverState.activeCategory ||
            hoverState.activeCategory === category.categoryName) &&
          (!hoverState.activeLabel || hoverState.activeLabel === item.label);
      }

      if (shouldShow) {
        const dataKey = `values.${item.key}.value`;
        currentTimePointKeys.add(dataKey);

        const value = timePoint.values[item.key]?.value;

        if (value !== null && value !== undefined && value !== "") {
          const payloadItem = payload.find((p) => p.dataKey === dataKey);

          if (payloadItem) {
            visibleItems.push({
              ...payloadItem,
              key: item.key,
              color: item.color,
              categoryName: category.categoryName,
              label: item.label
            });
          }
        }
      }
    });
  });

  return visibleItems.sort((a, b) => Number(b.value) - Number(a.value));
};

export const findMaxValue = <T extends string>(
  data: InteractiveLineChartData<T>
): number => {
  let maxValue = 0;

  data.timeSeriesData.forEach((timePoint) => {
    Object.values(timePoint.values).forEach((item) => {
      const value = Number(item.value);
      if (!isNaN(value) && value > maxValue) {
        maxValue = value;
      }
    });
  });

  return maxValue * 1.1;
};
